import { supabase } from '@/lib/supabase'
import type { Notification, NotificationType } from './types'

/**
 * Notifications API client (sidebar bell/inbox) backed by
 * `/api/v1/preinvest/notifications`. Mirrors the Supabase-bearer auth pattern of
 * `notes/api.ts`.
 */

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

const BASE = '/api/v1/preinvest/notifications'

export async function listNotifications(options?: {
  unread?: boolean
  limit?: number
  /** Repeated `?types=` filter (spec-35 phase 5, the bell's filter chips). */
  types?: NotificationType[]
}): Promise<Notification[]> {
  const params = new URLSearchParams()
  if (options?.unread) params.set('unread', 'true')
  if (options?.limit != null) params.set('limit', String(options.limit))
  for (const type of options?.types ?? []) params.append('types', type)
  const query = params.toString()
  const response = await fetch(query ? `${BASE}?${query}` : BASE, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Meldingen laden mislukt (${response.status})`)
  }
  return (await response.json()) as Notification[]
}

export async function getUnreadCount(): Promise<number> {
  const response = await fetch(`${BASE}/unread-count`, { headers: await authHeaders() })
  if (!response.ok) {
    throw new Error(`Aantal ongelezen laden mislukt (${response.status})`)
  }
  const data = (await response.json()) as { count: number }
  return data.count
}

export async function markRead(id: string): Promise<void> {
  const response = await fetch(`${BASE}/${id}/read`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Markeren als gelezen mislukt (${response.status})`)
  }
}

export async function markAllRead(): Promise<void> {
  const response = await fetch(`${BASE}/read-all`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Alles markeren als gelezen mislukt (${response.status})`)
  }
}

export async function deleteNotification(id: string): Promise<void> {
  const response = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Melding verwijderen mislukt (${response.status})`)
  }
}

/** Clear-all (spec-35); `readOnly` and `types` scope it to "Gelezen wissen" in a filter (spec-64-u2). */
export async function clearAllNotifications(
  options: { readOnly?: boolean; types?: NotificationType[] | null } = {},
): Promise<void> {
  const params = new URLSearchParams()
  if (options.readOnly) params.set('read_only', 'true')
  for (const type of options.types ?? []) params.append('types', type)
  const query = params.toString()
  const response = await fetch(query ? `${BASE}/clear-all?${query}` : `${BASE}/clear-all`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Meldingen wissen mislukt (${response.status})`)
  }
}
